/**
 * INYRON MOTION FIELD CORE
 *
 * A clip is raw material, not the final movement. Every living/controlled entity
 * owns a deterministic motor field that continuously reshapes cadence, stride,
 * posture, gaze, contact precision and secondary motion from body plan + intent.
 *
 * The implementation deliberately produces smooth, bounded modulation only.
 * It never rewrites skin weights, bind poses or bone hierarchies.
 */

import { HarmonicFieldRuntime, type HarmonicFieldFrame } from './HarmonicField'
import { PerceptualMorphogenesisFieldRuntime, type PerceptualMorphogenesisFrame } from './PerceptualMorphogenesisField'

export type BodyPlan =
  | 'humanoid'
  | 'quadruped'
  | 'avian'
  | 'aquatic'
  | 'arthropod'
  | 'serpentine'
  | 'amorphous'
  | 'colossus'
  | 'synthetic-organic'
  | 'flying'
  | 'custom'

export type MotorActionFamily =
  | 'idle'
  | 'walk'
  | 'run'
  | 'sprint'
  | 'strafe'
  | 'jump'
  | 'land'
  | 'climb'
  | 'swim'
  | 'fly'
  | 'attack'
  | 'defend'
  | 'interact'
  | 'rest'
  | 'custom'

export interface MotorPersonality {
  assertiveness: number
  caution: number
  curiosity: number
  expressiveness: number
  precision: number
  grace: number
}

export interface MotorFieldProfile {
  identity: string
  bodyPlan: BodyPlan
  seed: number
  stature: number
  massScale: number
  strideBias: number
  cadenceBias: number
  asymmetry: number
  elasticity: number
  secondaryMotion: number
  personality: MotorPersonality
}

export interface MotorThoughtState {
  action: MotorActionFamily
  speed01: number
  urgency: number
  arousal: number
  confidence: number
  fatigue: number
  pain: number
  curiosity: number
  threat: number
  socialWarmth: number
  focus: number
  turn01: number
  slope01: number
  airborne01: number
  contactIntent01: number
  targetBearing01: number
}

export interface MotorFieldFrame {
  /** Multiply AnimationAction.timeScale by this value. */
  cadenceScale: number
  /** Relative stride reach, for procedural/root-motion consumers. */
  strideScale: number
  /** Small vertical body pulse, normalized to stature. */
  verticalPulse: number
  /** Small side-to-side weight transfer, normalized to stature. */
  lateralSway: number
  /** Signed forward/backward body lean in radians. */
  forwardLean: number
  /** Signed anticipatory turn bias in radians. */
  turnLead: number
  /** Head motion leads body rotation by this signed amount. */
  headLead: number
  /** Tiny gaze offset; deliberately independent of headLead. */
  gazeMicro: number
  /** Relative arm/forelimb swing amount. */
  limbSwing: number
  /** 0..1 hand/paw/grip tension. */
  gripTension: number
  /** Signed pelvis/core transfer. */
  coreShift: number
  /** Breathing multiplier around 1. */
  breathScale: number
  /** Secondary motion driver for tails/wings/tendrils/etc. */
  secondaryPhase: number
  /** 0..1; higher means more exact contact/foot/hand placement. */
  contactPrecision: number
  /** 0..1 start offset used when entering looping clips. */
  loopPhaseOffset: number
  /** Shared high-order harmonic projection for motion/camera/VFX/audio/physics/AI. */
  harmonic: HarmonicFieldFrame
  /** Shared perceptual-morphogenesis projection for material/light/VFX/LOD/micro-motion. */
  perceptual: PerceptualMorphogenesisFrame
  /** Deterministic fingerprint for diagnostics / replay validation. */
  signature: string
}

const clamp = (n: number, lo = 0, hi = 1): number =>
  Math.max(lo, Math.min(hi, Number.isFinite(n) ? n : lo))

const clampSigned = (n: number): number => Math.max(-1, Math.min(1, Number.isFinite(n) ? n : 0))
const smooth = (t: number): number => t * t * (3 - 2 * t)
const fract = (n: number): number => n - Math.floor(n)

function hash32(input: string): number {
  let h = 2166136261 >>> 0
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  h ^= h >>> 16
  h = Math.imul(h, 0x7feb352d)
  h ^= h >>> 15
  h = Math.imul(h, 0x846ca68b)
  h ^= h >>> 16
  return h >>> 0
}

function unitHash(seed: number, index: number, channel: number): number {
  let x = (seed ^ Math.imul(index + 0x9e3779b9, 0x85ebca6b) ^ Math.imul(channel + 1, 0xc2b2ae35)) >>> 0
  x ^= x >>> 16
  x = Math.imul(x, 0x7feb352d)
  x ^= x >>> 15
  x = Math.imul(x, 0x846ca68b)
  x ^= x >>> 16
  return (x >>> 0) / 4294967295
}

/** Smooth deterministic value noise in [-1, 1]. */
function smoothNoise(seed: number, timeS: number, secondsPerCell: number, channel: number): number {
  const x = Math.max(0, Number.isFinite(timeS) ? timeS : 0) / secondsPerCell
  const i = Math.floor(x)
  const t = smooth(fract(x))
  const a = unitHash(seed, i, channel) * 2 - 1
  const b = unitHash(seed, i + 1, channel) * 2 - 1
  return a + (b - a) * t
}

function bodyPlanDefaults(bodyPlan: BodyPlan): Pick<MotorFieldProfile, 'stature' | 'massScale' | 'strideBias' | 'cadenceBias' | 'asymmetry' | 'elasticity' | 'secondaryMotion'> {
  switch (bodyPlan) {
    case 'quadruped': return { stature: .82, massScale: 1.1, strideBias: 1.08, cadenceBias: 1.08, asymmetry: .18, elasticity: .72, secondaryMotion: .72 }
    case 'avian': return { stature: .72, massScale: .65, strideBias: .9, cadenceBias: 1.18, asymmetry: .22, elasticity: .88, secondaryMotion: .92 }
    case 'aquatic': return { stature: .9, massScale: 1.0, strideBias: 1.15, cadenceBias: .72, asymmetry: .16, elasticity: .9, secondaryMotion: .95 }
    case 'arthropod': return { stature: .5, massScale: .55, strideBias: .7, cadenceBias: 1.32, asymmetry: .28, elasticity: .55, secondaryMotion: .42 }
    case 'serpentine': return { stature: .62, massScale: .82, strideBias: 1.25, cadenceBias: .78, asymmetry: .35, elasticity: 1.0, secondaryMotion: 1.0 }
    case 'amorphous': return { stature: .8, massScale: .85, strideBias: .8, cadenceBias: .75, asymmetry: .45, elasticity: 1.0, secondaryMotion: 1.0 }
    case 'colossus': return { stature: 2.5, massScale: 3.5, strideBias: 1.35, cadenceBias: .48, asymmetry: .12, elasticity: .35, secondaryMotion: .7 }
    case 'synthetic-organic': return { stature: 1.05, massScale: 1.25, strideBias: 1.0, cadenceBias: .9, asymmetry: .1, elasticity: .42, secondaryMotion: .55 }
    case 'flying': return { stature: 1.0, massScale: .85, strideBias: 1.1, cadenceBias: .96, asymmetry: .22, elasticity: .9, secondaryMotion: 1.0 }
    case 'custom': return { stature: 1, massScale: 1, strideBias: 1, cadenceBias: 1, asymmetry: .2, elasticity: .7, secondaryMotion: .7 }
    case 'humanoid':
    default: return { stature: 1, massScale: 1, strideBias: 1, cadenceBias: 1, asymmetry: .14, elasticity: .68, secondaryMotion: .45 }
  }
}

export function createMotorFieldProfile(
  identity: string,
  bodyPlan: BodyPlan = 'humanoid',
  overrides: Partial<Omit<MotorFieldProfile, 'identity' | 'bodyPlan' | 'seed'>> = {},
): MotorFieldProfile {
  const seed = hash32(identity || 'inyron-entity')
  const base = bodyPlanDefaults(bodyPlan)
  const personality: MotorPersonality = {
    assertiveness: .45 + unitHash(seed, 1, 1) * .35,
    caution: .25 + unitHash(seed, 2, 2) * .5,
    curiosity: .35 + unitHash(seed, 3, 3) * .5,
    expressiveness: .3 + unitHash(seed, 4, 4) * .55,
    precision: .45 + unitHash(seed, 5, 5) * .45,
    grace: .4 + unitHash(seed, 6, 6) * .45,
    ...(overrides.personality ?? {}),
  }
  return {
    identity,
    bodyPlan,
    seed,
    stature: overrides.stature ?? base.stature,
    massScale: overrides.massScale ?? base.massScale,
    strideBias: overrides.strideBias ?? base.strideBias,
    cadenceBias: overrides.cadenceBias ?? base.cadenceBias,
    asymmetry: overrides.asymmetry ?? base.asymmetry,
    elasticity: overrides.elasticity ?? base.elasticity,
    secondaryMotion: overrides.secondaryMotion ?? base.secondaryMotion,
    personality,
  }
}

function actionEnergy(action: MotorActionFamily): number {
  switch (action) {
    case 'rest': return .05
    case 'idle': return .12
    case 'walk': return .34
    case 'interact': return .28
    case 'defend': return .48
    case 'strafe': return .58
    case 'run': return .72
    case 'climb': return .76
    case 'swim': return .68
    case 'fly': return .72
    case 'jump': return .86
    case 'land': return .82
    case 'attack': return .92
    case 'sprint': return 1
    default: return .5
  }
}

function quant(n: number): string {
  return Math.round(n * 10000).toString(36)
}

/**
 * Stateless-by-time motor field. Given the same identity, time and thought
 * state it produces the same frame, which keeps replays/network prediction
 * reproducible while still avoiding synchronized loop playback.
 */
export class MotorFieldRuntime {
  readonly profile: MotorFieldProfile
  readonly harmonicField: HarmonicFieldRuntime
  readonly perceptualField: PerceptualMorphogenesisFieldRuntime

  constructor(profile: MotorFieldProfile | string, bodyPlan: BodyPlan = 'humanoid') {
    this.profile = typeof profile === 'string' ? createMotorFieldProfile(profile, bodyPlan) : profile
    this.harmonicField = new HarmonicFieldRuntime('motor:' + this.profile.identity, {
      quality: 'high',
      maxOrder: 64,
      sampleRateHz: 240,
    })
    this.perceptualField = new PerceptualMorphogenesisFieldRuntime('perceptual:' + this.profile.identity, 'high')
  }

  sample(timeS: number, thought: Partial<MotorThoughtState> = {}): MotorFieldFrame {
    const p = this.profile
    const t = Math.max(0, Number.isFinite(timeS) ? timeS : 0)
    const state: MotorThoughtState = {
      action: thought.action ?? 'idle',
      speed01: clamp(thought.speed01 ?? 0),
      urgency: clamp(thought.urgency ?? 0),
      arousal: clamp(thought.arousal ?? .2),
      confidence: clamp(thought.confidence ?? .5),
      fatigue: clamp(thought.fatigue ?? 0),
      pain: clamp(thought.pain ?? 0),
      curiosity: clamp(thought.curiosity ?? p.personality.curiosity),
      threat: clamp(thought.threat ?? 0),
      socialWarmth: clamp(thought.socialWarmth ?? 0),
      focus: clamp(thought.focus ?? .5),
      turn01: clampSigned(thought.turn01 ?? 0),
      slope01: clampSigned(thought.slope01 ?? 0),
      airborne01: clamp(thought.airborne01 ?? 0),
      contactIntent01: clamp(thought.contactIntent01 ?? 0),
      targetBearing01: clampSigned(thought.targetBearing01 ?? 0),
    }

    const energy = actionEnergy(state.action)
    const effort = clamp(energy * .58 + state.speed01 * .42 + state.urgency * .18 + state.arousal * .12)
    const impairment = clamp(state.fatigue * .42 + state.pain * .55)
    const precisionState = clamp(p.personality.precision * .55 + state.focus * .35 + state.contactIntent01 * .35 - impairment * .25)
    const expressive = clamp(p.personality.expressiveness * .65 + state.arousal * .35)
    const massDamping = 1 / Math.sqrt(Math.max(.3, p.massScale))
    const tensionState = clamp(
      state.threat * .42 +
      state.arousal * .24 +
      state.pain * .2 +
      state.contactIntent01 * .14,
    )
    const harmonic = this.harmonicField.sample(t, {
      energy01: effort,
      tension01: tensionState,
      coherence01: precisionState,
      detail01: clamp(.32 + state.focus * .46 + state.contactIntent01 * .22),
      distance01: 0,
      coupling01: clamp(.18 + state.arousal * .4 + state.contactIntent01 * .42),
      phaseOffset01: unitHash(p.seed, 0, 83),
    })
    const perceptual = this.perceptualField.sample(t, {
      salience01: clamp(.28 + state.focus * .34 + state.urgency * .2 + state.contactIntent01 * .24),
      motion01: clamp(state.speed01 * .62 + effort * .38),
      occlusion01: 0,
      distance01: 0,
      contrast01: clamp(.32 + state.threat * .3 + state.arousal * .2),
      exposure01: .5,
      surfaceComplexity01: clamp(.42 + p.secondaryMotion * .28 + p.elasticity * .18),
      runtimePressure01: 0,
    }, harmonic)

    // Independent smooth-noise bands plus the bounded shared harmonic field. Cell sizes are intentionally
    // incommensurate, so the combined gait has no short loop period.
    const nFast = smoothNoise(p.seed, t, .37, 11)
    const nStep = smoothNoise(p.seed, t, .83, 17)
    const nBody = smoothNoise(p.seed, t, 1.91, 23)
    const nMood = smoothNoise(p.seed, t, 4.37, 31)
    const nFine = smoothNoise(p.seed, t, .173, 47)
    const nSecondary = smoothNoise(p.seed, t, .619, 59)

    const cadenceScale = clamp(
      p.cadenceBias *
      (1 + nStep * .045 * (1 - precisionState * .35)) *
      (1 + state.urgency * .08 + state.arousal * .045 - impairment * .12),
      .45,
      1.7,
    )

    const strideScale = clamp(
      p.strideBias *
      (1 + nBody * .065 * p.elasticity + nMood * .025) *
      (1 + state.confidence * .045 - state.fatigue * .08),
      .55,
      1.6,
    )

    const verticalPulse = (
      (Math.sin(t * (6.2 * cadenceScale) + p.seed * 0.000013) * .5 + nFast * .32 + harmonic.motionPulse * .18) *
      .024 * p.stature * effort * massDamping * (0.65 + p.elasticity * .35)
    )

    const lateralSway = (
      (Math.sin(t * (3.13 * cadenceScale) + p.seed * 0.000021) * .45 + nBody * .4 + harmonic.bands.low * .15) *
      .034 * p.stature * (0.45 + expressive * .55) * (1 + p.asymmetry * nMood)
    )

    const forwardLean = clampSigned(
      state.speed01 * .38 +
      state.urgency * .2 +
      state.threat * p.personality.assertiveness * .1 -
      state.fatigue * .16 -
      state.pain * .12 +
      state.slope01 * .13 +
      nBody * .035,
    ) * .34

    const turnLead = clampSigned(
      state.turn01 * (.65 + state.focus * .25) +
      state.targetBearing01 * .18 +
      nStep * .035 * p.asymmetry,
    ) * .36

    const headLead = clampSigned(
      state.targetBearing01 * (.35 + state.focus * .45) +
      state.curiosity * nMood * .17 +
      state.threat * nFast * .08,
    ) * .42

    const gazeMicro = clampSigned(
      nFine * (.055 + state.curiosity * .06 + state.threat * .035) *
      (1 - state.focus * .55) +
      harmonic.cameraImpulse * .035,
    )

    const limbSwing = clamp(
      effort * (.45 + p.elasticity * .42) *
      (1 + nStep * .12 + state.arousal * .08) *
      massDamping,
      0,
      1.45,
    )

    const gripTension = clamp(
      .12 +
      state.contactIntent01 * .52 +
      state.threat * .35 +
      state.arousal * .18 +
      state.pain * .14 +
      p.personality.assertiveness * .08 +
      nFine * .025,
    )

    const coreShift = clampSigned(
      lateralSway / Math.max(.001, .034 * p.stature) * .42 +
      state.turn01 * .28 +
      nFast * p.asymmetry * .18,
    )

    const breathScale = clamp(
      1 +
      state.arousal * .15 +
      state.urgency * .07 +
      state.threat * .1 +
      state.fatigue * .18 +
      nMood * .025,
      .82,
      1.55,
    )

    const secondaryPhase = clampSigned(
      nSecondary * p.secondaryMotion * (.5 + effort * .4) +
      Math.sin(t * (1.41 + p.elasticity * .77) + p.seed * .000033) * .26 * p.secondaryMotion +
      harmonic.waveform * .16 * p.secondaryMotion,
    )

    const contactPrecision = clamp(
      precisionState *
      (1 - Math.abs(nFine) * .035) *
      (1 - state.airborne01 * .25),
    )

    // Entry phase changes by action family AND slow time epoch. This prevents
    // every run/walk transition from restarting at the exact same frame.
    const epoch = Math.floor(t / 2.731)
    const loopPhaseOffset = fract(
      unitHash(p.seed, epoch, hash32(state.action) & 0xff) +
      state.speed01 * .173 +
      state.turn01 * .071 +
      state.arousal * .037,
    )

    const signature = [
      p.seed.toString(36),
      state.action,
      quant(cadenceScale),
      quant(strideScale),
      quant(verticalPulse),
      quant(lateralSway),
      quant(forwardLean),
      quant(turnLead),
      quant(headLead),
      quant(gazeMicro),
      quant(coreShift),
      quant(secondaryPhase),
      quant(contactPrecision),
      quant(loopPhaseOffset),
      harmonic.signature,
      perceptual.signature,
    ].join(':')

    return {
      cadenceScale,
      strideScale,
      verticalPulse,
      lateralSway,
      forwardLean,
      turnLead,
      headLead,
      gazeMicro,
      limbSwing,
      gripTension,
      coreShift,
      breathScale,
      secondaryPhase,
      contactPrecision,
      loopPhaseOffset,
      harmonic,
      perceptual,
      signature,
    }
  }
}

export function motorActionFromAnimationState(state: string): MotorActionFamily {
  const s = String(state || '').toLowerCase()
  if (s.includes('sprint')) return 'sprint'
  if (s.includes('run') || s.includes('jog')) return 'run'
  if (s.includes('walk')) return 'walk'
  if (s.includes('strafe') || s.includes('backstep') || s.includes('dodge')) return 'strafe'
  if (s.includes('jump')) return 'jump'
  if (s.includes('land')) return 'land'
  if (s.includes('climb') || s.includes('hang')) return 'climb'
  if (s.includes('swim')) return 'swim'
  if (s.includes('fly') || s.includes('glide') || s.includes('soar')) return 'fly'
  if (s.includes('attack') || s.includes('combo') || s.includes('cast') || s.includes('special') || s.includes('ultimate')) return 'attack'
  if (s.includes('block') || s.includes('parry') || s.includes('guard')) return 'defend'
  if (s.includes('interact') || s.includes('gesture') || s.includes('talk')) return 'interact'
  if (s.includes('rest') || s.includes('sleep')) return 'rest'
  return 'idle'
}
